import React, { useEffect, useState, useRef } from 'react';
import { MapContainer, TileLayer, Marker, Popup, useMap } from 'react-leaflet';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { 
    Box, 
    Paper, 
    Typography, 
    Button, 
    CircularProgress,
    Chip,
    IconButton
} from '@mui/material';
import { ArrowBack, Refresh } from '@mui/icons-material';
import 'leaflet/dist/leaflet.css';
import L from 'leaflet';
import markerIcon from 'leaflet/dist/images/marker-icon.png';
import markerShadow from 'leaflet/dist/images/marker-shadow.png';
import markerRetina from 'leaflet/dist/images/marker-icon-2x.png';

// Fix Leaflet default icon issue with Webpack
delete (L.Icon.Default.prototype as any)._getIconUrl;
L.Icon.Default.mergeOptions({
    iconUrl: markerIcon,
    iconRetinaUrl: markerRetina,
    shadowUrl: markerShadow,
});

interface DeviceMapData {
    id: string;
    name: string;
    deviceTypeName?: string;
    latitude: number;
    longitude: number;
    childCount: number;
}

// Component to handle map centering
const MapCenterController: React.FC<{ center: [number, number]; zoom: number }> = ({ center, zoom }) => {
    const map = useMap();
    useEffect(() => {
        map.setView(center, zoom);
    }, [center, zoom, map]);
    return null;
};

const DeviceMap: React.FC = () => {
    const [devices, setDevices] = useState<DeviceMapData[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [searchParams] = useSearchParams();
    const auth = useAuth();
    const deviceIdParam = searchParams.get('deviceId');

    const fetchDevices = async () => {
        setLoading(true);
        setError(null);
        try {
            const response = await fetch('/api/devices/map', {
                headers: {
                    'Authorization': `Bearer ${auth.tokens?.accessToken}`
                }
            });

            if (!response.ok) {
                throw new Error('Failed to fetch device locations');
            }

            const data = await response.json();
            setDevices(data);
        } catch (err: any) {
            setError(err.message || 'An error occurred');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchDevices();
    }, []);

    // Calculate center point and zoom based on devices
    const getMapCenter = (): [number, number] => {
        // If deviceId is specified, center on that device
        if (deviceIdParam) {
            const targetDevice = devices.find(d => d.id === deviceIdParam);
            if (targetDevice) {
                return [targetDevice.latitude, targetDevice.longitude];
            }
        }

        if (devices.length === 0) {
            return [39.8283, -98.5795]; // Center of USA as default
        }

        const avgLat = devices.reduce((sum, d) => sum + d.latitude, 0) / devices.length;
        const avgLng = devices.reduce((sum, d) => sum + d.longitude, 0) / devices.length;
        return [avgLat, avgLng];
    };

    const getZoomLevel = (): number => {
        // If deviceId is specified, zoom closer
        if (deviceIdParam && devices.find(d => d.id === deviceIdParam)) {
            return 15;
        }

        if (devices.length <= 1) return 10;
        
        // Calculate bounds
        const lats = devices.map(d => d.latitude);
        const lngs = devices.map(d => d.longitude);
        const latRange = Math.max(...lats) - Math.min(...lats);
        const lngRange = Math.max(...lngs) - Math.min(...lngs);
        const maxRange = Math.max(latRange, lngRange);

        // Rough zoom level calculation
        if (maxRange > 10) return 4;
        if (maxRange > 5) return 5;
        if (maxRange > 2) return 6;
        if (maxRange > 1) return 7;
        if (maxRange > 0.5) return 8;
        if (maxRange > 0.1) return 10;
        return 12;
    };

    if (loading) {
        return (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
                <CircularProgress />
            </Box>
        );
    }

    if (error) {
        return (
            <Paper sx={{ p: 3, m: 2 }}>
                <Typography color="error">{error}</Typography>
                <Button onClick={fetchDevices} sx={{ mt: 2 }}>
                    Retry
                </Button>
            </Paper>
        );
    }

    return (
        <Box sx={{ height: 'calc(100vh - 100px)', display: 'flex', flexDirection: 'column', p: 2 }}>
            <Paper sx={{ p: 2, mb: 2, display: 'flex', alignItems: 'center', gap: 2 }}>
                <IconButton onClick={() => navigate('/devices')} size="small">
                    <ArrowBack />
                </IconButton>
                <Typography variant="h5" sx={{ flexGrow: 1 }}>
                    Device Map
                </Typography>
                <Chip 
                    label={`${devices.length} device${devices.length !== 1 ? 's' : ''}`} 
                    color="primary" 
                    size="small" 
                />
                <IconButton onClick={fetchDevices} size="small">
                    <Refresh />
                </IconButton>
            </Paper>

            {devices.length === 0 ? (
                <Paper sx={{ p: 3 }}>
                    <Typography>
                        No devices with location data found. Add latitude and longitude settings to your devices to see them on the map.
                    </Typography>
                </Paper>
            ) : (
                <Paper sx={{ flexGrow: 1, p: 0, overflow: 'hidden', minHeight: 500 }}>
                    <MapContainer
                        center={getMapCenter()}
                        zoom={getZoomLevel()}
                        style={{ height: '100%', width: '100%', minHeight: '500px' }}
                    >
                        <MapCenterController center={getMapCenter()} zoom={getZoomLevel()} />
                        <TileLayer
                            attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                        />
                        {devices.map((device) => (
                            <Marker
                                key={device.id}
                                position={[device.latitude, device.longitude]}
                            >
                                <Popup>
                                    <Box sx={{ minWidth: 200 }}>
                                        <Typography variant="h6" gutterBottom>
                                            {device.name}
                                        </Typography>
                                        {device.deviceTypeName && (
                                            <Typography variant="body2" color="text.secondary" gutterBottom>
                                                Type: {device.deviceTypeName}
                                            </Typography>
                                        )}
                                        {device.childCount > 0 && (
                                            <Typography variant="body2" color="text.secondary" gutterBottom>
                                                {device.childCount} child device{device.childCount !== 1 ? 's' : ''}
                                            </Typography>
                                        )}
                                        <Typography variant="caption" display="block" gutterBottom>
                                            {device.latitude.toFixed(6)}, {device.longitude.toFixed(6)}
                                        </Typography>
                                        <Button
                                            variant="contained"
                                            size="small"
                                            onClick={() => navigate(`/devices/${device.id}`)}
                                            sx={{ mt: 1 }}
                                            fullWidth
                                        >
                                            View Details
                                        </Button>
                                    </Box>
                                </Popup>
                            </Marker>
                        ))}
                    </MapContainer>
                </Paper>
            )}
        </Box>
    );
};

export default DeviceMap;
